import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { environment } from 'projects/semeris/src/environments/environment';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { RootState } from '../../../store/root-state.model';
import { resolveTabScope } from '../../shared/utils/tab-workspace-storage';

/**
 * States this tab's workspace on every API call, so two tabs on one session can hold different
 * workspaces. The id alone is the claim: the server resolves it to an asset scope, because only it
 * can say which assets a workspace covers, and a tab opening a link knows the id before anything
 * has told it the assets.
 *
 * Before the store resolves, `resolveTabScope()` stands in, the same source `pre-fetch.js` claimed
 * with, so every request from this tab names one workspace.
 */
@Injectable()
export class AssetInterceptor implements HttpInterceptor {
  private readonly store = inject<Store<RootState>>(Store);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.includes(environment.apiUrl) && !req.url.includes(environment.graphqlUrl)) {
      return next.handle(req);
    }

    return this.store.select(AppSelectors.resolvedWorkspaceScope).pipe(
      take(1),
      switchMap((storeScope) => {
        // Until the store resolves, claim from the tab's cached scope: the source `pre-fetch.js`
        // already claimed with before Angular booted, so both state one workspace per tab.
        const workspaceId = storeScope?.workspaceId ?? resolveTabScope()?.workspaceId;

        if (!workspaceId) {
          return next.handle(req);
        }

        return next.handle(req.clone({ headers: req.headers.set('X-Semeris-Workspace', workspaceId) }));
      }),
    );
  }
}
